import { PUBLIC_COMMON_API_BASE_PATH } from '$env/static/public';
import Constants from '$lib/constants';
import { browser, dev } from '$app/environment';
import * as Sentry from '@sentry/sveltekit';
import { get } from 'svelte/store';
import { locale, _ } from 'svelte-i18n';
import axios from 'axios';
import type { AxiosInstance, CreateAxiosDefaults, InternalAxiosRequestConfig } from 'axios';
import { createAxiosInstance, toastDanger } from '$lib/utils';
import { alertStorageShortage } from '$lib/service';
import { getRequestErrorContext, isAbortLikeError, isTimeoutLikeError } from './requestError';

import { actionSignOut, auth } from '$stores/auth';

const STATUS_BADREQUEST = 400;
const STATUS_UNAUTHORIZED = 401;
const STATUS_FORBIDDEN = 403;
const ERROR_ID = crypto.randomUUID();

let isShowingUnauthorizedError = false;
// 후속 에러를 무시하기 위한 플래그
let ignoreTailError = false;

export function createServiceAxiosInstance(serviceRoute: string, config?: CreateAxiosDefaults) {
    const baseURL = `${Constants.COMMON_API_HOST}${PUBLIC_COMMON_API_BASE_PATH}${serviceRoute}`;
    const instance = createAxiosInstance({ baseURL, timeout: 30000, withCredentials: true, ...config });

    setupServiceInterceptors(instance);

    return { instance, baseURL };
}

function setupServiceInterceptors(instance: AxiosInstance) {
    instance.interceptors.response.use(
        async function (response) {
            return response;
        },
        async function (error) {
            // 사용자 의도적 취소(AbortController, CancelToken)는 에러 처리하지 않음
            if (axios.isCancel(error) || isAbortLikeError(error)) {
                return Promise.reject(error);
            }

            // 2xx 외의 범위에 있는 상태 코드는 이 함수를 트리거 합니다.
            // 응답 오류가 있는 작업 수행

            const { url, ignoreRedirect } = error.config;

            // 인증 오류
            // 브라우저에서만 동작
            if (browser) {
                const status = error.response?.status;

                const unauthorized = status === STATUS_UNAUTHORIZED && !url.includes('/login/v');

                if (unauthorized || (status === STATUS_FORBIDDEN && url?.includes('/refresh/v'))) {
                    const unauthorizedError: any = new Error(get(_)('apps.contract.lib.api.helper.unauthorizedError'));
                    unauthorizedError.config = { ...error.config, ignoreSentry: true };

                    if (!isShowingUnauthorizedError) {
                        isShowingUnauthorizedError = true;
                        // PWALoader 마운트 이슈로 goto 대신 signOut쪽 리다이렉트를 goto 대신 location.href로 변경하면서, 토스트가 살짝 플래시 되고 사라지는 이슈가 있어서, sign-in 에서 에러 토스트하도록 변경. 때문에 아래 주석 처리
                        showError(unauthorizedError);

                        if (!ignoreRedirect && !url?.includes('/refresh-after/v') && !url?.includes('/logout/v')) {
                            await actionSignOut();
                        }
                    }
                } else if (status === STATUS_FORBIDDEN) {
                    // 권한 오류
                    const forbiddenError: any = new Error(get(_)('apps.contract.lib.api.helper.forbiddenError'));
                    forbiddenError.config = { ...error.config };
                    showError(forbiddenError);
                } else if (status === STATUS_BADREQUEST && error.response?.data.code === 'ERROR_EXCEEDED_STORAGE_LIMIT') {
                    // 아카이브 업로드시 용량제한 초과 코드를 수신하는 경우
                    if (!ignoreTailError) {
                        // 첫 에러에서, 후속에러를 무시하겠다는 flag 설정
                        ignoreTailError = true;
                        alertStorageShortage().then(closed => {
                            if (closed) {
                                // 모달 close 또는 첫 토스트 메시지 표시 후 초기화
                                ignoreTailError = false;
                            }
                        });
                    }
                } else if (isTimeoutLikeError(error)) {
                    // 요청 시간 초과(timeout) 오류
                    const timeoutError: any = new Error(get(_)('apps.contract.lib.api.helper.timeoutError', { values: { timeout: error.config.timeout } }));
                    timeoutError.code = error.code;
                    timeoutError.response = error.response;
                    timeoutError.config = { ...error.config };
                    showError(timeoutError);
                } else {
                    showError(error);
                }
            }

            return Promise.reject(error);
        },
    );

    instance.interceptors.request.use(
        async function (config: InternalAxiosRequestConfig) {
            // 공통 헤더 설정
            setCommonHeaders(config);

            isShowingUnauthorizedError = false;
            return config;
        },
        function (error) {
            // 요청 에러 핸들링
            return Promise.reject(error);
        },
    );
}

function setCommonHeaders(config) {
    const { tenantId, workspaceId } = get(auth);
    const localeValue = get(locale);

    // 추출한 정보를 헤더에 설정, Request Header에 설정된 값이 있으면 우선 사용
    config.headers['X-Tenant'] = config.headers['X-Tenant'] ?? (tenantId || 0);
    config.headers['X-Workspace'] = config.headers['X-Workspace'] ?? (workspaceId || 0);
    config.headers['X-Locale'] = config.headers['X-Locale'] ?? (localeValue || 'ko');
}

async function showError(error) {
    let blobErrorMsg = '';
    if (error.response && error.response.data && error.response.data instanceof Blob) {
        // responseType blob 일때 Blob 형식의 에러 메시지 처리
        try {
            const text = await error.response.data.text();
            blobErrorMsg = JSON.parse(text).message;
        } catch (err) {
            blobErrorMsg = '';
        }
    }
    const errorMsg = error.response?.data.message || blobErrorMsg || error.message;

    const ignoreErrorMessage = error.config?.ignoreErrorMessage;
    if (!ignoreErrorMessage) {
        toastDanger(errorMsg);
    }

    const ignoreSentry = error.config?.ignoreSentry;
    if (!dev && !ignoreSentry) {
        const requestErrorContext = getRequestErrorContext(error);
        Sentry.captureException(error, {
            extra: {
                ERROR_ID,
                ...requestErrorContext.extra,
            },
            fingerprint: requestErrorContext.fingerprint,
        });
    }
}
